import { useState, useRef, useEffect, useCallback } from "react";
import { Upload, Download, AlertCircle, Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  SCORING_CSV_BOM,
  SCORING_CSV_DELIMITER_DEFAULT,
  SCORING_CSV_HEADER,
} from "@/lib/scoringCsv";
import { saveScoringRulesForAnswer } from "@/services/scoring-rules";
import type { QuizQuestion } from "@/types/quiz";
import type { ScoringRule, ScoringRuleInput } from "@/types/scoring-rules";

interface ScoringImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImportComplete: (count: number) => void;
  quizId: string;
  questions: QuizQuestion[];
  existingRules: ScoringRule[];
}

interface ParsedRow {
  lineNumber: number;
  questionKey: string;
  answerValue: string;
  tag: string;
  weight: number;
  answerId: string;
  questionId: string;
  error?: string;
}

interface ParseResult {
  validRows: ParsedRow[];
  errorRows: ParsedRow[];
  skippedCount: number;
}

const REQUIRED_HEADERS = SCORING_CSV_HEADER;

function decodeCSVBuffer(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return new TextDecoder("utf-8").decode(bytes.slice(3));
  }
  return new TextDecoder("utf-8").decode(bytes);
}

function detectDelimiter(headerLine: string): string {
  const semicolons = (headerLine.match(/;/g) || []).length;
  const commas = (headerLine.match(/,/g) || []).length;
  return semicolons >= commas ? SCORING_CSV_DELIMITER_DEFAULT : ",";
}

function parseCSVLine(line: string, delimiter: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === delimiter) {
      result.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  result.push(current);
  return result;
}

export function parseScoringCSV(
  text: string,
  questions: QuizQuestion[],
): ParseResult {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());

  if (lines.length < 2) {
    return {
      validRows: [],
      errorRows: [
        {
          lineNumber: 1,
          questionKey: "",
          answerValue: "",
          tag: "",
          weight: 0,
          answerId: "",
          questionId: "",
          error:
            "Die Datei muss mindestens eine Kopfzeile und eine Datenzeile enthalten.",
        },
      ],
      skippedCount: 0,
    };
  }

  const delimiter = detectDelimiter(lines[0]);
  const headers = parseCSVLine(lines[0], delimiter).map((h) => h.trim());

  const missing = REQUIRED_HEADERS.filter((h) => !headers.includes(h));
  if (missing.length > 0) {
    return {
      validRows: [],
      errorRows: [
        {
          lineNumber: 1,
          questionKey: "",
          answerValue: "",
          tag: "",
          weight: 0,
          answerId: "",
          questionId: "",
          error: `Fehlende Spalten: ${missing.join(", ")}`,
        },
      ],
      skippedCount: 0,
    };
  }

  const idx = {
    key: headers.indexOf("Frage-Key"),
    answer: headers.indexOf("Antwort-Wert"),
    tag: headers.indexOf("Tag"),
    weight: headers.indexOf("Gewicht"),
  };

  // Build lookup maps from quiz questions
  const questionMap = new Map<string, QuizQuestion>();
  const answerMap = new Map<string, string>(); // "questionKey:optionValue" -> option.id
  for (const q of questions) {
    questionMap.set(q.questionKey, q);
    for (const opt of q.options ?? []) {
      answerMap.set(`${q.questionKey}:${opt.value}`, opt.id);
    }
  }

  const validRows: ParsedRow[] = [];
  const errorRows: ParsedRow[] = [];
  let skippedCount = 0;
  const seen = new Set<string>();

  for (let i = 1; i < lines.length; i++) {
    const cols = parseCSVLine(lines[i], delimiter);
    const lineNumber = i + 1;
    const questionKey = (cols[idx.key] ?? "").trim();
    const answerValue = (cols[idx.answer] ?? "").trim();
    const tag = (cols[idx.tag] ?? "").trim();
    const weightStr = (cols[idx.weight] ?? "").trim();

    // Skip empty template rows (no tag and no weight)
    if (!tag && !weightStr) {
      skippedCount++;
      continue;
    }

    const baseRow: ParsedRow = {
      lineNumber,
      questionKey,
      answerValue,
      tag,
      weight: 0,
      answerId: "",
      questionId: questionKey,
    };

    if (!questionMap.has(questionKey)) {
      errorRows.push({
        ...baseRow,
        error: "Frage-Key nicht im Quiz gefunden",
      });
      continue;
    }

    const lookupKey = `${questionKey}:${answerValue}`;
    const answerId = answerMap.get(lookupKey);
    if (!answerId) {
      errorRows.push({
        ...baseRow,
        error: "Antwort-Wert nicht in dieser Frage gefunden",
      });
      continue;
    }

    if (!tag) {
      errorRows.push({
        ...baseRow,
        answerId,
        error: "Tag darf nicht leer sein",
      });
      continue;
    }

    const weight = parseInt(weightStr, 10);
    if (Number.isNaN(weight)) {
      errorRows.push({
        ...baseRow,
        answerId,
        error: "Gewicht muss eine Ganzzahl sein",
      });
      continue;
    }

    const dupeKey = `${questionKey}:${answerValue}:${tag}`;
    if (seen.has(dupeKey)) {
      errorRows.push({
        ...baseRow,
        weight,
        answerId,
        error: "Doppelte Kombination Frage-Key + Antwort-Wert + Tag",
      });
      continue;
    }
    seen.add(dupeKey);

    validRows.push({
      ...baseRow,
      weight,
      answerId,
      questionId: questionKey,
    });
  }

  return { validRows, errorRows, skippedCount };
}

function downloadTemplate(questions: QuizQuestion[]) {
  const header = SCORING_CSV_HEADER.join(SCORING_CSV_DELIMITER_DEFAULT);
  const rows: string[] = [];
  for (const q of questions) {
    for (const opt of q.options ?? []) {
      rows.push(`${q.questionKey};${opt.value};;`);
    }
  }
  const csv = `${SCORING_CSV_BOM}${header}\n${rows.join("\n")}\n`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "quiznudge-scoring-vorlage.csv";
  a.click();
  URL.revokeObjectURL(url);
}

export function ScoringImportDialog({
  open,
  onClose,
  onImportComplete,
  quizId,
  questions,
  existingRules,
}: ScoringImportDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const [isDragOver, setIsDragOver] = useState(false);
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setParseResult(null);
    setFileError(null);
    setIsDragOver(false);
    setIsImporting(false);
    setImportError(null);
  }, [open]);

  const handleFile = useCallback(
    async (file: File) => {
      setFileError(null);
      setParseResult(null);
      setImportError(null);

      if (
        file.type !== "text/csv" &&
        file.type !== "application/vnd.ms-excel" &&
        !file.name.endsWith(".csv")
      ) {
        setFileError("Bitte eine CSV-Datei auswählen.");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setFileError("Die Datei ist zu groß. Maximal 5 MB erlaubt.");
        return;
      }

      try {
        const buffer = await file.arrayBuffer();
        const text = decodeCSVBuffer(buffer);
        const result = parseScoringCSV(text, questions);
        setParseResult(result);
      } catch {
        setFileError("Die Datei konnte nicht gelesen werden.");
      }
    },
    [questions],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      e.target.value = "";
    },
    [handleFile],
  );

  const handleImport = useCallback(async () => {
    if (!parseResult || parseResult.validRows.length === 0) return;
    setIsImporting(true);
    setImportError(null);

    try {
      // Group imported rows by answerId
      const importGroups = new Map<
        string,
        { questionId: string; rows: ParsedRow[] }
      >();
      for (const row of parseResult.validRows) {
        if (!importGroups.has(row.answerId)) {
          importGroups.set(row.answerId, {
            questionId: row.questionId,
            rows: [],
          });
        }
        importGroups.get(row.answerId)!.rows.push(row);
      }

      // For each answerId group, merge with existing rules and save
      for (const [answerId, { questionId, rows }] of importGroups) {
        const existingForAnswer = existingRules.filter(
          (r) => r.answerId === answerId,
        );
        const importedTags = new Set(rows.map((r) => r.tag));

        // Keep existing rules not covered by import
        const kept: ScoringRuleInput[] = existingForAnswer
          .filter((r) => !importedTags.has(r.targetValue))
          .map((r, i) => ({
            targetType: "tag" as const,
            targetValue: r.targetValue,
            weight: r.weight,
            reasonText: r.reasonText,
            sortOrder: i,
            active: r.active,
          }));

        // Add imported rules (new or updated)
        const imported: ScoringRuleInput[] = rows.map((r, i) => ({
          targetType: "tag" as const,
          targetValue: r.tag,
          weight: r.weight,
          reasonText: null,
          sortOrder: kept.length + i,
          active: true,
        }));

        await saveScoringRulesForAnswer(answerId, {
          quizId,
          questionId,
          rules: [...kept, ...imported],
        });
      }

      queryClient.invalidateQueries({
        queryKey: ["scoring-rules", quizId],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin", "scoring-preview", quizId],
      });

      onImportComplete(parseResult.validRows.length);
    } catch (err) {
      setImportError(
        err instanceof Error
          ? err.message
          : "Beim Import ist ein Fehler aufgetreten.",
      );
    } finally {
      setIsImporting(false);
    }
  }, [parseResult, existingRules, quizId, queryClient, onImportComplete]);

  if (!open) return null;

  const validCount = parseResult?.validRows.length ?? 0;
  const errorCount = parseResult?.errorRows.length ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
        }}
        role="button"
        tabIndex={-1}
        aria-label="Dialog schließen"
      />
      <div className="relative z-50 mx-4 max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg border bg-background p-6 shadow-lg">
        <h2 className="text-lg font-semibold">Scoring-Regeln importieren</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Lade eine CSV-Datei mit deinen Scoring-Regeln hoch. Jede Zeile ordnet
          einer Antwort einen Tag mit Gewicht zu.
        </p>

        {/* Template Download */}
        <div className="mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadTemplate(questions)}
          >
            <Download className="mr-2 h-4 w-4" />
            CSV-Vorlage herunterladen
          </Button>
        </div>

        {/* Upload Zone */}
        {!parseResult && (
          <div
            role="button"
            tabIndex={0}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            className={cn(
              "mt-4 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors",
              isDragOver
                ? "border-primary bg-primary/5"
                : "border-muted-foreground/25 hover:border-muted-foreground/50",
            )}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleFileChange}
            />
            <Upload className="h-8 w-8 text-muted-foreground" />
            <p className="mt-3 text-sm font-medium">
              Datei hierhin ziehen oder klicken zum Auswählen
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              CSV-Datei, Semikolon- oder Komma-getrennt
            </p>
          </div>
        )}

        {/* File Error */}
        {fileError && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <p className="text-sm text-destructive">{fileError}</p>
          </div>
        )}

        {/* Parse Results */}
        {parseResult && (
          <>
            <div className="mt-4 flex items-center gap-4 text-sm">
              <span className="font-medium text-green-700">
                {validCount} gültige {validCount === 1 ? "Regel" : "Regeln"}
              </span>
              {errorCount > 0 && (
                <span className="text-red-600">
                  {errorCount} {errorCount === 1 ? "Fehler" : "Fehler"}
                </span>
              )}
              {(parseResult.skippedCount ?? 0) > 0 && (
                <span className="text-muted-foreground">
                  {parseResult.skippedCount} leere Zeilen übersprungen
                </span>
              )}
            </div>

            {/* Preview Table */}
            {(validCount > 0 || errorCount > 0) && (
              <div className="mt-3 max-h-64 overflow-auto rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Zeile</th>
                      <th className="px-3 py-2 text-left font-medium">
                        Frage-Key
                      </th>
                      <th className="px-3 py-2 text-left font-medium">
                        Antwort-Wert
                      </th>
                      <th className="px-3 py-2 text-left font-medium">Tag</th>
                      <th className="px-3 py-2 text-left font-medium">
                        Gewicht
                      </th>
                      <th className="px-3 py-2 text-left font-medium" />
                    </tr>
                  </thead>
                  <tbody>
                    {parseResult.validRows.map((row) => (
                      <tr key={row.lineNumber} className="border-t">
                        <td className="px-3 py-1.5 text-muted-foreground">
                          {row.lineNumber}
                        </td>
                        <td className="px-3 py-1.5 font-mono text-xs">
                          {row.questionKey}
                        </td>
                        <td className="px-3 py-1.5 font-mono text-xs">
                          {row.answerValue}
                        </td>
                        <td className="px-3 py-1.5">{row.tag}</td>
                        <td className="px-3 py-1.5 text-center">
                          {row.weight}
                        </td>
                        <td className="px-3 py-1.5" />
                      </tr>
                    ))}
                    {parseResult.errorRows.map((row) => (
                      <tr
                        key={`err-${row.lineNumber}`}
                        className="border-t bg-red-50"
                      >
                        <td className="px-3 py-1.5 text-muted-foreground">
                          {row.lineNumber}
                        </td>
                        <td className="px-3 py-1.5 font-mono text-xs">
                          {row.questionKey}
                        </td>
                        <td className="px-3 py-1.5 font-mono text-xs">
                          {row.answerValue}
                        </td>
                        <td className="px-3 py-1.5">{row.tag}</td>
                        <td className="px-3 py-1.5 text-center">
                          {row.weight || ""}
                        </td>
                        <td className="px-3 py-1.5 text-xs text-red-600">
                          {row.error}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Import Error */}
            {importError && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <p className="text-sm text-destructive">{importError}</p>
              </div>
            )}

            <div className="mt-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setParseResult(null);
                  setImportError(null);
                }}
              >
                Andere Datei wählen
              </Button>
            </div>
          </>
        )}

        {/* Footer */}
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={isImporting}>
            Abbrechen
          </Button>
          {parseResult && validCount > 0 && (
            <Button onClick={handleImport} disabled={isImporting}>
              {isImporting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Wird importiert...
                </>
              ) : (
                `${validCount} Scoring-Regeln importieren`
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
